/**
 * This functionality was cloned from `platform/packages/technology-innovation/charlie-table/src/shared-hooks/use-infinite-scroll.ts`
 * in the absence of any Atlaskit component that could do the same thing.
 *
 * In the future it may be worth contributing to Atlassian's design system.
 */

import { useCallback, useEffect, useRef } from 'react';

/*
How Infinite pagination works:
1) On mount, a single IntersectionObserver is created that persists for the component's lifetime

2) When a DOM node is provided via the ref callback, the observer begins watching that node

3) When the observed element becomes visible, the observer calls the latest onScrollTo callback

4) If the node changes, the observer unobserves the old node and observes the new one (without recreating the observer)

Source: Followed some similar logic from EoC and Confluence's infinite scroll implementation.  https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/confluence/next/packages/infinite-scroll/src/InfiniteScroll.tsx
*/

/**
 * UseInfiniteScroll is a hook that observes an element and calls a function when it is intersecting.
 * This is useful for infinite scrolling.
 *
 * UseInfiniteScroll also accepts a type parameter that allows you to specify the type of the element being observed.
 *
 * @param onScrollTo: Function that is called when the element is intersecting
 * @returns loadMoreRef: Ref to the element that is being observed
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLElement>({
	onScrollTo,
}: {
	onScrollTo: () => void;
}): (node: T | null) => void {
	const observerRef = useRef<IntersectionObserver | null>(null);
	const onScrollToRef = useRef(onScrollTo);
	const currentNodeRef = useRef<T | null>(null);

	// This function is called by the IntersectionObserver API when "loaderTarget" UI element
	// changes visibility.  If it has become visible (typically by user scrolling), call our
	// scroll handler and the parent will attempt to load more data
	const fetchMoreCallback = useCallback((loadMoreEntry: IntersectionObserverEntry[]) => {
		// Sometimes based on an unknown render race condition, the previous IntersectionObserver is not
		// properly cleared before the next one is observed, which causes the 0th indexed IntersectionObserver
		// to never be intersecting, which causes the onScrollTo callback to not be called when it should be.
		// To fix this just get rid of the old observer if it is not properly cleaned up.
		if (loadMoreEntry.length > 1) {
			loadMoreEntry.shift();
		}

		if (loadMoreEntry?.[0]?.isIntersecting) {
			onScrollToRef.current();
		}
	}, []);

	const loadMoreRefCallback = useCallback((node: T | null) => {
		const prevNode = currentNodeRef.current;
		const observer = observerRef.current;

		currentNodeRef.current = node;

		if (!observer) {
			return;
		}

		if (prevNode) {
			observer.unobserve(prevNode);
		}

		if (node) {
			observer.observe(node);
		}
	}, []);

	useEffect(() => {
		onScrollToRef.current = onScrollTo;
	}, [onScrollTo]);

	useEffect(() => {
		observerRef.current = new IntersectionObserver(fetchMoreCallback);

		// If there's already a node stored, observe it now that the observer exists
		if (currentNodeRef.current) {
			observerRef.current.observe(currentNodeRef.current);
		}

		// cleanup on unmount
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [fetchMoreCallback]);

	return loadMoreRefCallback;
}
